import { action } from '@ember/object'
import { service } from '@ember/service'
import Component from '@glimmer/component'
import { flatMap } from 'lodash'
import type RouterService from '@ember/routing/router-service'
import type TeacherService from 'teacher-ui-client/services/teacher'
import type { IntlService } from 'ember-intl'
import type { ParseResult } from 'papaparse'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { pick } from 'teacher-ui-client/helpers/pick'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { ErrorsTable } from 'teacher-ui-client/components/csv-to-records/errors-table'
import { SuccessTable } from 'teacher-ui-client/components/csv-to-records/success-table'
import type { TeacherWithCsvMeta } from 'teacher-ui-client/services/teacher'

interface Signature {
  Element: HTMLFormElement
}

/**
 * This component prompts for a csv file to be chosen, delegates parsing to the csv service,
 * presents the table of parsed csv rows, enables selection of records to be created from the parsed csv,
 * fires onSuccess and onCancel handlers as needed and displays error messages when the entire csv file
 * cannot be parsed or a given row cannot be parsed.
 *
 */
export class CsvToRecords extends Component<Signature> {
  @service teacher!: TeacherService

  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  @service router!: RouterService

  get importer() {
    return this.teacher.importFromCsv.last
  }

  get filename() {
    return this.teacher.file?.name
  }

  get isParsing() {
    return this.importer?.isRunning
  }

  get isSuccessful() {
    return this.importer?.isSuccessful
  }

  get isError() {
    return this.importer?.isError
  }

  get error() {
    return this.importer?.error
  }

  get rows() {
    return this.importer?.value ?? []
  }

  // get the TeacherWithCsvMeta from the ChangesetFor<TeacherWithCsvMeta>
  get rowsUnwrapped(): TeacherWithCsvMeta[] {
    return this.importer?.value?.map((entry) => entry.data) ?? []
  }

  get hasRows() {
    return this.rows.length > 0
  }

  get hasValidationErrors() {
    return this.validationErrors.length > 0
  }

  get validationErrors(): { index: string; message: string }[] {
    const errorsForRows = this.rows.flatMap((rowChangeset) => {
      // If there are parser errors, show those. Else, show any changeset errors (like validation issues)
      const parserErrors = (rowChangeset.parseResult as ParseResult<unknown>).errors
      if (parserErrors.length) {
        return parserErrors.map((error) => {
          const message = this.intl.t(`csv.errors.${error.code}`)
          return { index: rowChangeset.get('rowIndex') as string, message }
        })
      } else {
        const rowErrors = rowChangeset.errors
        const validationMessages = flatMap(rowErrors, (error) => error.validation)
        return validationMessages.map((message) => ({
          index: rowChangeset.get('rowIndex') as string,
          message: message as string,
        }))
      }
    })

    return errorsForRows
  }

  get manageTeachersUrl() {
    return this.router.urlFor('manage.teachers')
  }

  /**
   * When the component is no longer needed the TaskInstance for parsing is notified
   * so that the parsed rows and csv file data are released.
   */
  willDestroy() {
    super.willDestroy()
    this.teacher.cleanupImport()
  }

  @action
  fileAdded(fileArray: File[]) {
    if (fileArray && fileArray.length > 0) {
      const file = fileArray[0]
      void this.teacher.importFromCsv.perform(this.subscriptionType.current, file)
    }
  }

  <template>
    <form novalidate ...attributes>
      {{#if this.isError}}
        <div data-test-error class="border-x-watermelony-red-200 bg-watermelony-red-100 mb-5 rounded-sm border p-4">
          <p class="text-watermelony-red-400">
            {{t "manage.bulkActions.csv.rowErrorUnknown"}}
          </p>
          <p class="text-watermelony-red-400">
            {{t "manage.bulkActions.csv.rowErrorSuggestion"}}
          </p>
        </div>
      {{else if this.hasValidationErrors}}
        <div data-test-warning class="border-yolky-yellow-200 bg-yolky-yellow-100 mb-5 rounded-sm border p-4">
          <p class="text-yolky-yellow-400">
            {{t "manage.bulkActions.csv.invalid"}}
          </p>
        </div>

        <ErrorsTable
          class="w-full"
          @rows={{this.validationErrors}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />

        <label for="upload-csv" class="mt-4">
          <input
            id="upload-csv"
            class="hidden"
            type="file"
            accept="text/csv, .csv"
            {{on "change" (pick "target.files" this.fileAdded)}}
          />
          <UiButtonLink @appearance={{ButtonAppearance.regular}}>
            {{t "manage.bulkActions.csv.uploadAnother"}}
          </UiButtonLink>
        </label>
      {{else if this.isSuccessful}}
        {{#if this.hasRows}}
          <div data-test-success class="border-grapey-green-200 bg-grapey-green-100 mb-5 rounded-sm border p-4">
            <p class="text-grapey-green-400">
              {{t
                "manage.teachers.bulkActions.csv.successMessage"
                count=this.rows.length
                url=this.manageTeachersUrl
                htmlSafe=true
              }}
            </p>
          </div>
        {{/if}}

        <SuccessTable
          class="w-full"
          @rows={{this.rowsUnwrapped}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />

        <label for="upload-csv" class="mt-4">
          <input
            id="upload-csv"
            class="hidden"
            type="file"
            accept="text/csv, .csv"
            {{on "change" (pick "target.files" this.fileAdded)}}
          />
          <UiButtonLink @appearance={{ButtonAppearance.regular}}>
            {{t "manage.bulkActions.csv.uploadAnother"}}
          </UiButtonLink>
        </label>
      {{else if this.isParsing}}
        <FidgetLoadingIndicator @show={{true}} @overlay={{false}} @centered={{true}} />
      {{/if}}
    </form>
  </template>
}

export default CsvToRecords
